/**
 * Admin Feature - Utility Functions
 */
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

// =====================================================
// DATE UTILS
// =====================================================

/**
 * Formata data para exibição
 */
export function formatDate(date, pattern = 'dd/MM/yyyy') {
	if (!date) return '-'
	const parsed = typeof date === 'string' ? parseISO(date) : date
	return format(parsed, pattern, { locale: ptBR })
}

/**
 * Formata data e hora
 */
export function formatDateTime(date) {
	return formatDate(date, 'dd/MM/yyyy HH:mm')
}

/**
 * Formata data relativa (ex: "há 2 horas")
 */
export function formatRelativeTime(date) {
	if (!date) return '-'
	const parsed = typeof date === 'string' ? parseISO(date) : date
	return formatDistanceToNow(parsed, { addSuffix: true, locale: ptBR })
}

// =====================================================
// NUMBER UTILS
// =====================================================

/**
 * Formata número com separador de milhares
 */
export function formatNumber(num) {
	if (num === null || num === undefined) return '0'
	return new Intl.NumberFormat('pt-BR').format(num)
}

/**
 * Formata porcentagem
 */
export function formatPercent(value, decimals = 1) {
	if (value === null || value === undefined) return '0%'
	return `${value.toFixed(decimals)}%`
}

/**
 * Calcula variação percentual
 */
export function calcPercentChange(current, previous) {
	if (!previous) return 0
	return ((current - previous) / previous) * 100
}

// =====================================================
// STRING UTILS
// =====================================================

/**
 * Trunca texto
 */
export function truncate(text, maxLength = 50) {
	if (!text) return ''
	if (text.length <= maxLength) return text
	return `${text.substring(0, maxLength)}...`
}

/**
 * Capitaliza primeira letra
 */
export function capitalize(text) {
	if (!text) return ''
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

/**
 * Formata nome completo
 */
export function formatFullName(user) {
	if (!user) return '-'
	return `${user.name || ''} ${user.lastname || ''}`.trim() || user.username || '-'
}

/**
 * Formata endereço
 */
export function formatAddress(location) {
	if (!location) return '-'
	const parts = [location.city, location.state].filter(Boolean)
	return parts.join(', ') || '-'
}

/**
 * Formata número de telefone
 */
export function formatPhoneNumber(phone) {
	if (!phone) return '-'
	const cleaned = phone.replace(/\D/g, '')
	if (cleaned.length === 11) {
		return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
	}
	if (cleaned.length === 10) {
		return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
	}
	return phone
}

/**
 * Formata valor monetário
 */
export function formatCurrency(value) {
	if (value === null || value === undefined) return 'R$ 0,00'
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	}).format(value)
}

/**
 * Trunca texto (alias para truncate)
 */
export function truncateText(text, maxLength = 50) {
	return truncate(text, maxLength)
}

// =====================================================
// VALIDATION UTILS
// =====================================================

/**
 * Valida email
 */
export function isValidEmail(email) {
	const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	return regex.test(email)
}

/**
 * Valida se é ID MongoDB válido
 */
export function isValidObjectId(id) {
	return /^[a-f\d]{24}$/i.test(id)
}

// =====================================================
// COLOR UTILS
// =====================================================

/**
 * Retorna cor baseada no status
 */
export function getStatusColor(status) {
	const colors = {
		active: 'green',
		inactive: 'gray',
		pending: 'yellow',
		completed: 'blue',
		cancelled: 'red',
		scheduled: 'blue',
		in_progress: 'green',
	}
	return colors[status] || 'gray'
}

/**
 * Retorna classe Tailwind para cor
 */
export function getColorClass(color, type = 'bg') {
	const colorMap = {
		green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
		red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
		blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
		yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
		gray: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
		purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
	}
	return colorMap[color]?.[type] || colorMap.gray[type]
}

// =====================================================
// EXPORT CSV
// =====================================================

/**
 * Exporta dados para CSV
 */
export function exportToCSV(data, filename, columns) {
	if (!data || !data.length) return

	const headers = columns.map((col) => col.label).join(',')
	const rows = data.map((item) =>
		columns.map((col) => {
			let value = col.accessor ? col.accessor(item) : item[col.key]
			// Escape aspas e vírgulas
			if (typeof value === 'string') {
				value = `"${value.replace(/"/g, '""')}"`
			}
			return value ?? ''
		}).join(',')
	)

	const csv = [headers, ...rows].join('\n')
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	link.href = URL.createObjectURL(blob)
	link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`
	link.click()
}

// =====================================================
// QUERY STRING UTILS
// =====================================================

/**
 * Converte objeto em query string
 */
export function objectToQueryString(obj) {
	return Object.entries(obj)
		.filter(([, value]) => value !== null && value !== undefined && value !== '')
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('&')
}

/**
 * Parse query string para objeto
 */
export function queryStringToObject(queryString) {
	if (!queryString) return {}
	return Object.fromEntries(new URLSearchParams(queryString))
}

const adminUtils = {
	formatDate,
	formatDateTime,
	formatRelativeTime,
	formatNumber,
	formatPercent,
	formatFullName,
	formatAddress,
	formatPhoneNumber,
	formatCurrency,
	truncate,
	truncateText,
	capitalize,
	getStatusColor,
	getColorClass,
	exportToCSV,
}

export default adminUtils
